import {
  useLazyRehabForms,
  useLazyTransitionalCare,
} from "apollo/hooks/queries";
import {
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { useEnvContext } from "context/EnvContext";
import { activateDTAAttachments } from "core/model/utils/featureFlags";
import { AllPatientForms, QueryProgress } from "core/types";
import { useEffect, useState } from "react";

export function useGetFormFiles({
  auctionId,
  isEligiblePage,
  patientId,
}: {
  auctionId: number;
  isEligiblePage?: boolean;
  patientId: number;
}) {
  const { isReceiverApp } = useEnvContext();
  const [formsProgress, setFormsProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [allForms, setAllForms] = useState<AllPatientForms | undefined>(
    undefined,
  );

  const getRehabForms = useLazyRehabForms();
  const getTransitionalCare = useLazyTransitionalCare();

  useEffect(() => {
    const shouldLoadForms =
      activateDTAAttachments && !isEligiblePage && !isReceiverApp;

    if (!shouldLoadForms) {
      setFormsProgress(QUERY_PROGRESS_NOT_STARTED);
      setAllForms(undefined);
      return;
    }

    const loadForms = async () => {
      setFormsProgress(QUERY_PROGRESS_PENDING);

      try {
        const [rehabFormsResult, transitionalCareResult] = await Promise.all([
          getRehabForms({ auctionId, patientId }),
          getTransitionalCare({ auctionId, patientId }),
        ]);

        setAllForms({
          ...rehabFormsResult,
          transitional_care_form: transitionalCareResult,
        });
        setFormsProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        console.error("Error loading forms:", error);
        setFormsProgress(QUERY_PROGRESS_NOT_STARTED);
      }
    };

    loadForms();
  }, [activateDTAAttachments, isEligiblePage, isReceiverApp, auctionId, patientId]);

  return {
    allForms,
    getFormsProgress: formsProgress,
  };
}
